import React, { useEffect } from "react";
import * as mathjs from "mathjs";
import { useParams, useHistory } from "react-router-dom"

// reactstrap components
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  CustomInput,
  Row,
  UncontrolledDropdown, 
  DropdownToggle, 
  DropdownMenu, 
  DropdownItem,
  Pagination,
  PaginationItem, 
  PaginationLink
} from "reactstrap";

import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableFooter,
  TableHead,
} from "@mui/material"

import SweetAlert from "react-bootstrap-sweetalert";
import Select from "react-select";
import StatisticChart from "components/StatisticChart/Boxplot";
import ReactDatetime from "react-datetime"

import LanguageDependentText from "components/TimerTable/LanguageDependentText";
import { useHuntTrackerContext } from "tracker-context";

import Flatpickr from "react-flatpickr";

import { SocketIO } from "sessions/socket"
import { SessionConfigs } from "sessions/sessionState";
import { extractPatchInfo, extractWorldInfo, extractHuntInfo, extractFATEInfo } from "sessions/gameInfo";
import { huntInfos } from "sessions/resources/hunts.js";

const StatisticPage = () => {
  const history = useHistory();

  const [alert, setAlert] = React.useState(null);
  const [controller, dispatch] = useHuntTrackerContext();
  const { language, dataCenter, user } = controller;

  const [data, setData] = React.useState(null);
  const [huntNames, setHuntNames] = React.useState([]);
  const [worldNames, setWorldNames] = React.useState([]);
  const [patchNames, setPatchNames] = React.useState([]);
  const [rankSelected, setRankSelected] = React.useState({value: "All", label: SessionConfigs.dictionaryLookup("header", "historyFilterAllRank")});
  const [timeSelection, setTimeSelection] = React.useState({pre: new Date(), post: new Date()});
  const [filterResult, setFilterResult] = React.useState({});
  const [rangeType, setRangeType] = React.useState({value: "All"});

  const textLibrary = SessionConfigs.dictionaryLookup;
  const patchList = extractPatchInfo();
  const worldList = extractWorldInfo(dataCenter)["Names"];

  React.useEffect(() => {
    SessionConfigs.queryData("/api/fetchStatistics", {
      datacenter: dataCenter,
      rangeType: rangeType.value
    }).then((response) => {
      setData(response.data);
    });
  }, [rangeType]);

  const filterSearchResults = (data) => {
    let worldSelected = worldNames.map((value)=>value.value);
    let huntSelected = huntNames.map((value)=>value.value);
    
    if (worldSelected.length == 0) {
      worldSelected = Object.keys(data);
    }

    if (huntSelected.length == 0) {
      huntSelected = Object.keys(data[worldSelected[0]]);
    }

    let filterResult = {};
    for (let world of worldSelected) {
      filterResult[world] = {};
      for (let hunt of huntSelected) {
        filterResult[world][hunt] = data[world][hunt].map((value) => value / 3600000);
        if (filterResult[world][hunt].length > 0) {
          const mean = mathjs.mean(...filterResult[world][hunt]);
          const std = mathjs.std(...filterResult[world][hunt]);
          filterResult[world][hunt] = filterResult[world][hunt].filter((value) => {
            return Math.abs(value - mean) / std < 3 && value > 10 && value < 1000;
          })
        }
      }
    }

    const patchSelected = patchNames.map((value)=>value.value);
    if (patchNames.length > 0) {
      for (let world of Object.keys(filterResult)) {
        for (let hunt of Object.keys(filterResult[world])) {
          if (!patchSelected.includes(huntInfos[hunt].Patch)) {
            delete filterResult[world][hunt];
          }
        }
      }
    }

    setFilterResult(filterResult);
  }

  React.useEffect(() => {
    if (data) filterSearchResults(data);
  }, [data, huntNames, worldNames, patchNames, rankSelected]);

  return (
    <>
      <div className="content">
        {alert}
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <Row>
                  <Col sm={12} md={6} lg={4}>
                    <LanguageDependentText header={"historyHuntFilter"} fontSize={15} />
                    <Select
                      className="react-select info"
                      classNamePrefix="react-select"
                      placeholder="Select Hunts"
                      name="Hunt Filter"
                      closeMenuOnSelect={false}
                      isMulti
                      isSearchable
                      minMenuHeight={600}
                      maxMenuHeight={900}
                      value={huntNames}
                      onChange={(value) => {
                        if (!value) setHuntNames([]);
                        else setHuntNames(value);
                      }}
                      options={Object.keys(huntInfos).map((key) => {
                        if (huntInfos[key].Rank == 3) {
                          return {value: key, label: textLibrary("huntName", key)};
                        }
                      }).filter((value) => value)}
                    />
                  </Col>
                  <Col sm={12} md={6} lg={4}>
                    <LanguageDependentText header={"historyWorldFilter"} fontSize={15} />
                    <Select
                      className="react-select info"
                      classNamePrefix="react-select"
                      name="World Filter"
                      closeMenuOnSelect={false}
                      isMulti
                      minMenuHeight={600}
                      maxMenuHeight={900}
                      value={worldNames}
                      onChange={(value) => {
                        if (!value) setWorldNames([]);
                        else setWorldNames(value);
                      }}
                      options={worldList.map((world)=>{
                        return {value: world, label: world}
                      })}
                    />
                  </Col>
                  <Col sm={12} md={6} lg={4}>
                    <LanguageDependentText header={"historyPatchFilter"} fontSize={15} />
                    <Select
                      className="react-select info"
                      classNamePrefix="react-select"
                      name="Patch Filter"
                      closeMenuOnSelect={false}
                      isMulti
                      minMenuHeight={600}
                      maxMenuHeight={900}
                      value={patchNames}
                      onChange={(value) => {
                        if (!value) setPatchNames([]);
                        else setPatchNames(value);
                      }}
                      options={Object.keys(patchList).map((key) => {
                        return {value: key, label: patchList[key][language]}
                      })}
                    />
                  </Col>
                  <Col sm={12} md={6} lg={4}>
                    <LanguageDependentText header={"historyRangeFilter"} fontSize={15} />
                    <Select
                      className="react-select info"
                      classNamePrefix="react-select"
                      name="Range Filter"
                      closeMenuOnSelect={false}
                      minMenuHeight={600}
                      maxMenuHeight={900}
                      value={rangeType}
                      onChange={(value) => {
                        setRangeType(value);
                      }}
                      options={["LastThreeMonth","All"].map((key) => {
                        return {value: key, label: SessionConfigs.dictionaryLookup("header", key, language)}
                      })}
                    />
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <StatisticChart dataToRender={filterResult} height={600} figureTitle={"statistics"} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default StatisticPage;
